import { ConversationMessageComponent } from 'features/chatbot/components/conversationMessage/ConversationMessage'
import { StreamingReferences } from 'features/chatbot/components/StreamingReferences'
import { ChatbotDataContext } from 'features/chatbot/contexts/ChatbotDataLayer'
import { ThinkingNote } from 'features/chatbot/types/conversation'
import React, { useContext, useEffect, useMemo, useRef } from 'react'
import styled, { keyframes } from 'styled-components'

const ConversationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 0.5rem;
`

const ThinkingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  padding: 0.25rem 0;
`

const fadeInOut = keyframes`
  0%, 20% { opacity: 0.3; }
  50% { opacity: 1; }
  80%, 100% { opacity: 0.3; }
`

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const Dot = styled.span<{ $delay: number }>`
  animation: ${fadeInOut} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`

const DotsContainer = styled.span`
  display: inline-flex;
  width: 1.25em;
`

const AnimatedDots: React.FC = () => {
  return (
    <DotsContainer>
      <Dot $delay={0}>.</Dot>
      <Dot $delay={0.2}>.</Dot>
      <Dot $delay={0.4}>.</Dot>
    </DotsContainer>
  )
}

const ThoughtMessage = styled.div`
  color: ${({ theme }) => theme.forecast.tertiaryText};
  padding: 0.125rem 0 0.125rem 0.5rem;
  border-left: 2px solid rgba(255, 255, 255, 0.1);
  margin-left: 0.25rem;
  font-size: 0.75rem;
  animation: ${fadeSlideIn} 0.3s ease-out;
`

const ThoughtsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`

interface ThinkingIndicatorProps {
  thoughts: ThinkingNote[]
  isThinking: boolean
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  thoughts,
  isThinking,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [thoughts])

  return (
    <div aria-live='polite' aria-atomic='true'>
      <ThinkingContainer>
        <span>{isThinking ? 'Thinking' : 'Stopped'}</span>
        {isThinking && <AnimatedDots />}
      </ThinkingContainer>
      {thoughts.length > 0 && (
        <ThoughtsContainer ref={containerRef}>
          {thoughts.map((note, index) => (
            <React.Fragment key={index}>
              {note.text && <ThoughtMessage>{note.text}</ThoughtMessage>}
              {note.sources && note.sources.length > 0 && (
                <StreamingReferences sources={note.sources} />
              )}
            </React.Fragment>
          ))}
        </ThoughtsContainer>
      )}
    </div>
  )
}

export const Conversation: React.FC = () => {
  const { conversation, isThinking, streamingThoughts } =
    useContext(ChatbotDataContext)

  const visibleMessages = useMemo(
    () =>
      conversation
        .map((msg, originalIndex) => ({ msg, originalIndex }))
        .filter(({ msg }) => msg.type !== 'thought'),
    [conversation]
  )

  const lastUserMessageIndex = useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].msg.type === 'userMessage') {
        return i
      }
    }
    return -1
  }, [visibleMessages])

  const activeThoughts: ThinkingNote[] = useMemo(() => {
    const thoughts: ThinkingNote[] = []
    for (let i = conversation.length - 1; i >= 0; i--) {
      const msg = conversation[i]
      if (msg.type === 'thought') {
        thoughts.unshift({ text: msg.text })
      } else {
        break
      }
    }
    return thoughts
  }, [conversation])

  // Merge conversation-based thoughts with streaming thoughts (which carry sources)
  const mergedThoughts: ThinkingNote[] = useMemo(() => {
    if (streamingThoughts.length > 0) return streamingThoughts
    return activeThoughts
  }, [activeThoughts, streamingThoughts])

  const hasTrailingThoughts = mergedThoughts.length > 0

  return (
    <ConversationContainer>
      {visibleMessages.map(({ msg, originalIndex }, index) => (
        <ConversationMessageComponent
          key={`${msg.type}-${originalIndex}`}
          message={msg}
          itemIndex={index}
          isLastUserMessage={index === lastUserMessageIndex}
        />
      ))}
      {(isThinking || hasTrailingThoughts) && (
        <ThinkingIndicator thoughts={mergedThoughts} isThinking={isThinking} />
      )}
    </ConversationContainer>
  )
}
